import React, {FC, useCallback} from 'react'

import {yupResolver} from '@hookform/resolvers/yup'
import cn from 'classnames/bind'
import {useRouter} from 'next/router'
import {FormProvider, useForm} from 'react-hook-form'
import * as yup from 'yup'

import FormFooter from 'components/blocks/FormFooter'
import FormHeader from 'components/blocks/FormHeader'
import Button from 'components/elements/Button'
import FieldInput from 'components/elements/fields/FieldInput'
import {useAuthStore} from 'src/utils/contexts/RootStore.context'
import useAsyncFlow from 'src/utils/hooks/useAsyncFlow'

import s from './ResetPasswordForm.module.scss'

const cx = cn.bind(s)

const schema = yup
	.object({
		email: yup.string().email('please enter a valid email').required(),
	})
	.required()

interface IResetPasswordFormProps {
	className?: string
}

const ResetPasswordForm: FC<IResetPasswordFormProps> = ({className}) => {
	const authStore = useAuthStore()
	const [resetPasswordFlow, {errorMessage, isLoading}] = useAsyncFlow<
		typeof authStore.resetPassword
	>(authStore.resetPassword)
	const router = useRouter()
	const methods = useForm<yup.TypeOf<typeof schema>>({
		resolver: yupResolver(schema),
		defaultValues: {
			email: '',
		},
	})

	const onSubmit = useCallback(
		async (data: yup.TypeOf<typeof schema>) => {
			const onSuccess = () => router.push('/auth/reset-password/success')
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			await resetPasswordFlow(data.email!)({onSuccess})
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[authStore, authStore.resetPassword, router],
	)

	return (
		<div className={cx('wrapper', className)}>
			<FormProvider {...methods}>
				<form className={s.form} data-testid="form-reset-password">
					<FormHeader
						className={cx('header')}
						title="password reset"
						subTitle="Enter the email address associated with your account and we’ll send you a link to reset your password"
						message={errorMessage || methods.formState.errors}
					/>

					<FieldInput
						wrapperClassName={cx('input-spaces')}
						name="email"
						type="email"
						label="Email address"
						placeholder="Write email address"
					/>

					<Button
						className={cx('btn')}
						size="big"
						onClick={methods.handleSubmit(onSubmit)}
						isLoading={isLoading}
						disabled={methods.formState.isSubmitted && !methods.formState.isValid}
						data-testid="btn-reset-password">
						Send
					</Button>
					<FormFooter />
				</form>
			</FormProvider>
		</div>
	)
}

ResetPasswordForm.displayName = 'ResetPasswordForm'

export default ResetPasswordForm
